import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaUndo } from "react-icons/fa";

import Button from "../../../../components/Utils/Button/Button";
import ButtonSwitcher, {
  ButtonSwitcherValue,
} from "../../../../components/Utils/ButtonSwitcher/ButtonSwitcher";
import StyledDropdown from "../../../../components/Utils/StyledDropdown/StyledDropdown";
import {
  StyledInputDate,
  StyledInputNumber,
  StyledInputText,
} from "../../../../components/Utils/StyledInput/StyledInput";
import StyledInputMultiFile from "../../../../components/Utils/StyledInputMultiFile/StyledInputMultiFile";
import useAccountingStore, {
  createInitialRevenue,
  getDefaultAccountingTvaRate,
} from "../../../../stores/AccountingStore";
import useLoadingStore from "../../../../stores/LoadingStore";
import useUserStore from "../../../../stores/UserStore";
import AccountingRevenueType from "../../../../types/AccountingRevenue";
import EventType from "../../../../types/Event";
import { formatDateParam } from "../../../../Utils/formatDateParam";
import appFetch from "../../../../Utils/Services/Fetch/appFetch";

interface AccountingFormProps {
  revenueToEdit: AccountingRevenueType | null;
  revenueType: ButtonSwitcherValue;
  partyList: EventType[];
}

// Record VAT either as a rate or as an absolute amount (for receipts mixing
// several VAT rates).
const TVA_MODES: ButtonSwitcherValue[] = [
  { id: "rate", name: "Taux (%)" },
  { id: "amount", name: "Montant (€)" },
];

const Revenue: React.FC<AccountingFormProps> = ({
  revenueToEdit,
  revenueType,
  partyList,
}) => {
  const [tvaMode, setTvaMode] = useState<ButtonSwitcherValue>(TVA_MODES[0]);

  const { loadingStates, setLoading } = useLoadingStore();
  const { revenue, setRevenue } = useAccountingStore();
  const { isVatSubject } = useUserStore();

  const handleTvaModeChange = (mode: ButtonSwitcherValue) => {
    setTvaMode(mode);
    setRevenue((prevRevenue) => ({
      ...prevRevenue,
      tva_rate:
        mode.id === "rate"
          ? (prevRevenue.tva_rate ?? getDefaultAccountingTvaRate())
          : null,
      tva_amount: mode.id === "amount" ? (prevRevenue.tva_amount ?? 0) : null,
    }));
  };

  const setBuyDate = (value: Date) => {
    setRevenue((prevRevenue) => {
      return { ...prevRevenue, recorded_date: value };
    });
  };

  const createRevenue = async (type: string) => {
    if (
      !revenue.product_name ||
      !revenue.unit_price ||
      !revenue.recorded_date
    ) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setLoading("createRevenue", true);

    const formData = new FormData();
    formData.append("product_name", revenue.product_name);
    formData.append("unit_price", revenue.unit_price.toString());
    // Send a calendar-day string (YYYY-MM-DD): recorded_date is a `date`
    // column, so serializing the picked day as a UTC instant would drift it.
    formData.append(
      "recorded_date",
      formatDateParam(revenue.recorded_date) ?? "",
    );
    if (isVatSubject) {
      if (tvaMode.id === "amount") {
        formData.append("tva_amount", String(revenue.tva_amount ?? 0));
      } else {
        formData.append("tva_rate", String(revenue.tva_rate ?? 0));
      }
    }
    if (revenue.payment_method) {
      formData.append("payment_method", revenue.payment_method);
    }
    formData.append("revenue_id", revenue.revenue_id.toString());
    formData.append("amount", revenue.amount.toString());
    if (revenue.event?.event_id) {
      formData.append("event_id", revenue.event.event_id.toString());
    }

    if (revenue.files && revenue.files.length > 0) {
      revenue.files.forEach((file) => {
        formData.append("files", file);
      });
    } else if (revenue.file) {
      formData.append("file", revenue.file);
    }

    try {
      const method = type === "addRevenue" ? "POST" : "PUT";
      const response = await appFetch(`/api/accounting/revenue/${type}`, {
        method,
        body: formData,
      });
      if (response) {
        handleReset();
        toast.success(response.message);
      }
    } catch (error) {
      console.error("Erreur lors de la création de la rentrée :", error);
    }
    setLoading("createRevenue", false);
  };

  useEffect(() => {
    const fetchQuoteDetails = async () => {
      if (!revenueToEdit) {
        return;
      }

      try {
        const revenueDetailData = await appFetch(
          `/api/accounting/revenue/getRevenueDetails?revenue_id=${revenueToEdit.revenue_id}`,
        );

        if (revenueDetailData && revenueDetailData.revenue) {
          let file = null;

          if (revenueDetailData.revenue.revenue.file_path) {
            const fileUrl = `/api/accounting/revenue/getRevenuesFile?revenue_id=${revenueDetailData.revenue.revenue.revenue_id}`;
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            file = new File(
              [blob],
              revenueDetailData.revenue.revenue.file_path,
              { type: blob.type },
            );
          }

          const loadedRevenue = revenueDetailData.revenue.revenue;
          const isAmountMode =
            loadedRevenue.tva_amount !== null &&
            loadedRevenue.tva_amount !== undefined;
          setTvaMode(isAmountMode ? TVA_MODES[1] : TVA_MODES[0]);

          setRevenue((prevRevenue) => ({
            ...prevRevenue,
            ...loadedRevenue,
            recorded_date: new Date(loadedRevenue.recorded_date),
            product_name: loadedRevenue.product_name,
            unit_price: loadedRevenue.unit_price,
            tva_rate: isAmountMode ? null : (loadedRevenue.tva_rate ?? 0),
            tva_amount: isAmountMode ? Number(loadedRevenue.tva_amount) : null,
            event: revenueDetailData.revenue.event,
            payment_method: revenueDetailData.revenue.revenue.payment_method,
            file,
          }));
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des détails de la rentrée :",
          error,
        );
      }
    };

    fetchQuoteDetails();
  }, [revenueToEdit, setRevenue]);

  useEffect(() => {
    if (revenueToEdit || tvaMode.id !== "rate") {
      return;
    }

    const defaultTvaRate = getDefaultAccountingTvaRate();
    setRevenue((prevRevenue) => {
      const isPristine =
        prevRevenue.revenue_id === -1 &&
        !prevRevenue.product_name &&
        !prevRevenue.unit_price &&
        !prevRevenue.amount &&
        !prevRevenue.payment_method &&
        !prevRevenue.event &&
        !prevRevenue.file &&
        (!prevRevenue.files || prevRevenue.files.length === 0);

      if (!isPristine || prevRevenue.tva_rate === defaultTvaRate) {
        return prevRevenue;
      }

      return {
        ...prevRevenue,
        tva_rate: defaultTvaRate,
      };
    });
  }, [isVatSubject, revenueToEdit, setRevenue, tvaMode.id]);

  const handleReset = () => {
    setTvaMode(TVA_MODES[0]);
    setRevenue(createInitialRevenue());
  };

  return (
    <>
      <div className="accountingSection">
        <StyledInputText
          label="Nom*"
          value={revenue.product_name}
          setValue={(value: string) => {
            setRevenue((prevRevenue) => {
              return { ...prevRevenue, product_name: value };
            });
          }}
        />
        <StyledInputNumber
          label="Prix unitaire TTC*"
          value={revenue.unit_price.toString()}
          setValue={(value: string | null) => {
            setRevenue((prevRevenue) => {
              return { ...prevRevenue, unit_price: Number(value) };
            });
          }}
        />
        <StyledInputNumber
          label="Quantité*"
          value={revenue.amount.toString()}
          setValue={(value: string | null) => {
            setRevenue((prevRevenue) => {
              return { ...prevRevenue, amount: Number(value) };
            });
          }}
        />
        {isVatSubject && (
          <div className="accountingTvaField">
            <ButtonSwitcher
              value={tvaMode}
              setValue={handleTvaModeChange}
              datas={TVA_MODES}
            />
            {tvaMode.id === "amount" ? (
              <StyledInputNumber
                label="Montant TVA (€)"
                value={(revenue.tva_amount ?? 0).toString()}
                setValue={(value: string | null) => {
                  setRevenue((prevRevenue) => {
                    return {
                      ...prevRevenue,
                      tva_amount: Number(value ?? 0),
                      tva_rate: null,
                    };
                  });
                }}
              />
            ) : (
              <StyledInputNumber
                label="TVA (%)"
                value={(revenue.tva_rate ?? 0).toString()}
                setValue={(value: string | null) => {
                  setRevenue((prevRevenue) => {
                    return {
                      ...prevRevenue,
                      tva_rate: Number(value ?? 0),
                      tva_amount: null,
                    };
                  });
                }}
              />
            )}
          </div>
        )}
        <StyledInputDate
          label="Date d'entrée*"
          value={revenue.recorded_date}
          setValue={setBuyDate}
        />
        <StyledDropdown
          label="Événement"
          value={revenue.event}
          setValue={(value: EventType | undefined) => {
            setRevenue((prevRevenue) => {
              return { ...prevRevenue, event: value };
            });
          }}
          itemList={partyList}
          itemLabelKey="name"
          itemIdKey="event_id"
        />
        <StyledDropdown
          label="Moyen de paiement*"
          value={{ payment_method: revenue.payment_method }}
          setValue={(value: { payment_method: string } | undefined) => {
            setRevenue((prevRevenue) => {
              return {
                ...prevRevenue,
                payment_method: value?.payment_method ?? "",
              };
            });
          }}
          itemList={[
            { payment_method: "Cash" },
            { payment_method: "Carte bancaire" },
            { payment_method: "Virement" },
          ]}
          itemLabelKey="payment_method"
          itemIdKey="payment_method"
        />
        <StyledInputMultiFile
          label="Justificatifs*"
          setValue={(files: File[]) => {
            setRevenue((prevRevenue) => {
              return { ...prevRevenue, files };
            });
          }}
          value={revenue.files || []}
          className="accountingFileInput"
          maxFiles={20}
          accept="image/*,application/pdf,.doc,.docx,.txt"
        />
      </div>
      <div className="accountingSection"></div>
      {!revenueToEdit && (
        <>
          <Button
            onClick={() => {
              createRevenue("addRevenue");
            }}
            className="submitBtn"
            label={`Ajouter la ${revenueType.id}`}
            isLoading={loadingStates.createRevenue}
          />
          <Button
            onClick={() => {
              handleReset();
            }}
            className="squaredButton"
            icon={<FaUndo />}
          />
        </>
      )}
      {revenueToEdit && (
        <Button
          onClick={() => {
            createRevenue("updateRevenue");
          }}
          className="submitBtn"
          label={`Modifider la ${revenueType.id}`}
          isLoading={loadingStates.createRevenue}
        />
      )}
    </>
  );
};

export default Revenue;
